/**
 * Read-only Campaign URL row with Copy + Test buttons. URL is derived from
 * the selected domain (resolved via /binom/domains) + the campaign key —
 * Binom's standard click link is "http(s)://<host>/click.php?key=<key>".
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo, useState } from 'react';
import { Copy, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { Input } from '@/components/ui/form/input/Input';
import { useBinomDomains } from '@/hooks/binom';

export interface BinomCampaignUrlFieldProps {
  domainUuid?: string;
  campaignKey?: string;
}

const BinomCampaignUrlField: FC<BinomCampaignUrlFieldProps> = ({
  domainUuid,
  campaignKey,
}) => {
  const domainsQuery = useBinomDomains();
  const [copied, setCopied] = useState(false);

  const url = useMemo(
    () => buildBinomCampaignUrl(domainsQuery.data, domainUuid, campaignKey),
    [domainsQuery.data, domainUuid, campaignKey],
  );

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex w-full items-center gap-1">
      <Input
        value={url}
        readOnly
        placeholder="No domain selected"
        className="h-7 flex-1 text-xs"
      />
      <Button
        size="sm"
        variant="outline"
        type="button"
        onClick={copy}
        disabled={!url}
        title={copied ? 'Copied!' : 'Copy link'}
        className="h-7 px-2 text-[11px]"
      >
        <Copy className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        variant="outline"
        type="button"
        disabled={!url}
        onClick={() => url && window.open(url, '_blank', 'noopener,noreferrer')}
        title="Test link"
        className="h-7 px-2 text-[11px]"
      >
        <ExternalLink className="h-3 w-3" />
      </Button>
    </div>
  );
};

export function buildBinomCampaignUrl(
  domains: Array<{ uuid: string; host: string }> | undefined,
  domainUuid: string | undefined,
  campaignKey: string | undefined,
): string {
  if (!campaignKey) return '';
  const host = (domains ?? []).find((d) => d.uuid === domainUuid)?.host;
  if (!host) return '';
  const normalized = host.startsWith('http') ? host : `http://${host}`;
  return `${normalized.replace(/\/$/, '')}/click.php?key=${encodeURIComponent(campaignKey)}`;
}

export default BinomCampaignUrlField;
